import React from "react";
import { Button, Space, Typography } from "antd";
import { FormOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import SectionCard from "../../../components/shared/SectionCard";
import StatusTag from "../../../components/shared/StatusTag";
import InlineLoading from "../../../components/shared/InlineLoading";
import EmptyState from "../../../components/shared/EmptyState";
import { DashboardPendingForm } from "../../../api/healthcare/DashboardApi";

const { Text } = Typography;

const getDaysAgo = (iso: string): string => {
	const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
	if (days === 0) return "Today";
	if (days === 1) return "Yesterday";
	return `${days}d ago`;
};

interface FormsInTrayPanelProps {
	pendingForms: DashboardPendingForm[];
	stats: { total: number; pending: number; in_progress: number; completed: number };
	isLoading: boolean;
	onOpenForm?: (form: DashboardPendingForm) => void;
}

const FormsInTrayPanel: React.FC<FormsInTrayPanelProps> = ({ pendingForms, stats, isLoading, onOpenForm }) => {
	const navigate = useNavigate();

	return (
		<SectionCard
			title={
				<Space size={6} align="center">
					<FormOutlined />
					<span>Forms In-Tray</span>
				</Space>
			}
			extra={
				<Button size="small" type="link" onClick={() => navigate("/healthcare/forms-intray")}>
					View all
				</Button>
			}
			style={{ height: "100%" }}
		>
			{isLoading ? (
				<InlineLoading minHeight={160} message="Loading forms…" />
			) : pendingForms.length === 0 ? (
				<EmptyState
					title="No pending forms"
					description="All assigned forms are up to date"
					style={{ padding: "24px 0" }}
				/>
			) : (
				<div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
					{pendingForms.map((form, idx) => {
						const isLast = idx === pendingForms.length - 1;
						return (
							<div
								key={form.id}
								onClick={() => onOpenForm?.(form)}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									padding: "8px 0",
									borderBottom: isLast ? "none" : "1px solid var(--mg-border)",
									cursor: "pointer",
								}}
							>
								<div style={{ flex: 1, minWidth: 0 }}>
									<Text style={{ fontSize: 12, fontWeight: 600, display: "block" }} ellipsis>
										{form.form?.title || `Form #${form.form_id}`}
									</Text>
									<Text type="secondary" style={{ fontSize: 11 }}>
										Patient #{form.patient_id} · {getDaysAgo(form.created_at)}
									</Text>
								</div>
								<StatusTag status={form.status} />
								<Button
									size="small"
									onClick={(e) => { e.stopPropagation(); onOpenForm?.(form); }}
									style={{ flexShrink: 0 }}
								>
									Open
								</Button>
							</div>
						);
					})}
				</div>
			)}
		</SectionCard>
	);
};

export default FormsInTrayPanel;
